/**
 * The questions the page asks about a value before it decides what to draw beside it: which value
 * the row shows and where it came from, does it need a person's attention, is it empty, and do the
 * two filters leave it on screen.
 *
 * Every answer is read off what the api already sends in TaxProfileFieldValue, so no component
 * works one out for itself and SectionIndex's counts, the "Needs review only" filter and the row's
 * tag always agree.
 *
 * A row shows one value: the approved one, or, where nothing is approved, the first waiting value,
 * which the api orders newest first and ValuePicker preselects.
 */

import type {
  TaxProfileCandidateGroup,
  TaxProfileFieldValue,
} from '@shared/types/taxProfile';

/** The waiting value the page preselects: the first group of `waiting`, the newest. */
export const newestWaiting = (
  field: TaxProfileFieldValue
): TaxProfileCandidateGroup | null => field.waiting[0] ?? null;

/** The value the row shows: the approved value, else the newest waiting value, else null. */
export const shownValue = (
  field: TaxProfileFieldValue
): string | number | boolean | null =>
  field.approved ? field.approved.value : newestWaiting(field)?.value ?? null;

/**
 * True when a value needs a person's attention: nothing is approved and a source gave a value
 * ('proposed'), or a value is approved and a source gave a different one ('changed').
 */
export const fieldNeedsReview = (field: TaxProfileFieldValue): boolean =>
  field.status === 'proposed' || field.status === 'changed';

/** True when the row shows no value, which is the value whose cell reads NOT_ON_FILE. */
export const fieldIsEmpty = (field: TaxProfileFieldValue): boolean => {
  const value = shownValue(field);
  return value === null || value === undefined || value === '';
};

/**
 * True when the two filters of the left column leave this value's row on screen. The sections draw
 * their rows by this rule and the count above them is made by it, so the accept-all button
 * accepts only what a person can actually see.
 */
export const fieldIsShown = (
  field: TaxProfileFieldValue | undefined,
  filters: { needsReviewOnly: boolean; hideEmpty: boolean }
): boolean => {
  if (!field) return false;
  if (filters.needsReviewOnly && !fieldNeedsReview(field)) return false;
  if (filters.hideEmpty && fieldIsEmpty(field)) return false;
  return true;
};
